import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

export interface StackRankingLiveItem {
  id: string;
  name: string;
  description?: string;
}

interface StackRankingLiveProps {
  items: StackRankingLiveItem[];
  ranking: string[];
  onRankingChange: (ranking: string[]) => void;
}

function SortableRow({
  item,
  rank,
}: {
  item: StackRankingLiveItem;
  rank: number;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: item.id,
  });
  const style = transform ? { transform: CSS.Transform.toString(transform) } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2  border px-3 py-2.5 ${
        isDragging
          ? 'border-electric-mustard-600/50 bg-electric-mustard-600/10 opacity-80'
          : 'border-[var(--rule-bright)] bg-[var(--surface)]'
      }`}
    >
      <span className="font-heading text-[0.55rem] w-5 text-alpine-green-400 font-bold">{rank}</span>
      <button
        type="button"
        className="text-[var(--text-ter)] hover:text-[var(--text-sec)] cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-body text-[0.82rem] font-medium text-[var(--text-sec)] truncate">{item.name}</div>
        {item.description && (
          <div className="font-body text-[0.68rem] text-[var(--text-ter)] truncate">{item.description}</div>
        )}
      </div>
    </div>
  );
}

export function StackRankingLive({ items, ranking, onRankingChange }: StackRankingLiveProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const orderedItems = ranking
    .map(id => items.find(i => i.id === id))
    .filter((i): i is StackRankingLiveItem => i != null);
  const unranked = items.filter(i => !ranking.includes(i.id));
  const displayList = [...orderedItems, ...unranked];

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = displayList.findIndex(i => i.id === active.id);
    const newIndex = displayList.findIndex(i => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(displayList, oldIndex, newIndex);
    onRankingChange(reordered.map(i => i.id));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={displayList.map(i => i.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {displayList.map((item, idx) => (
            <SortableRow key={item.id} item={item} rank={idx + 1} />
          ))}
        </div>
      </SortableContext>
      <p className="font-heading text-[0.48rem] tracking-[0.1em] uppercase text-[var(--text-dim)] text-center mt-4">
        Drag to rank — #1 is highest priority
      </p>
    </DndContext>
  );
}
